import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import { ReferralActionsForSubmission, ReferralPayouts } from './ReferralAdmin';

/**
 * AdminView
 * Protected admin dashboard for viewing and managing all submissions.
 * Requires authentication with admin role via app_metadata.is_admin === true.
 * Displays submissions in a table with status management and detail expansion.
 *
 * Uses supabase.auth.getUser() directly -- no auth-helpers-react dependency.
 */

interface Submission {
  id: string;
  full_legal_name: string;
  carrier_name: string;
  current_premium_amount: number | null;
  created_at: string;
  status: 'new' | 'in_progress' | 'call_scheduled' | 'completed' | 'cancelled';
  [key: string]: unknown;
}

interface ExpandedRows {
  [key: string]: boolean;
}

export default function AdminView() {
  const [user, setUser] = useState<User | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [isAdmin, setIsAdmin] = useState(false);

  // Check auth + admin status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        setUser(currentUser);

        if (!currentUser) {
          setLoading(false);
          return;
        }

        const adminStatus =
          currentUser.app_metadata?.is_admin === true ||
          currentUser.user_metadata?.is_admin === true;
        setIsAdmin(adminStatus);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fetch submissions if admin
  useEffect(() => {
    if (!isAdmin) return;

    const fetchSubmissions = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('submissions')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setSubmissions(data || []);
      } catch (err) {
        console.error('Error fetching submissions:', err);
        setError('Failed to load submissions');
      }
    };

    fetchSubmissions();
  }, [isAdmin]);

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      const { error: updateError } = await supabase
        .from('submissions')
        .update({ status: newStatus })
        .eq('id', id);

      if (updateError) throw updateError;

      setSubmissions((prev) =>
        prev.map((sub) =>
          sub.id === id ? { ...sub, status: newStatus as Submission['status'] } : sub
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const STATUS_LABELS: Record<string, string> = {
    new: 'New',
    in_progress: 'In Progress',
    call_scheduled: 'Call Scheduled',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'call_scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <p className="text-slate-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated or not admin
  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-slate-200">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Access Denied</h1>
            <p className="text-slate-600">
              You do not have permission to view this page. Only administrators can access the admin dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Admin Dashboard</h1>

        <div className="mb-8">
          <ReferralPayouts />
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {submissions.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-slate-200">
            <p className="text-slate-600">No submissions yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Carrier
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Premium
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <React.Fragment key={submission.id}>
                    {/* Main Row */}
                    <tr className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-900">{submission.full_legal_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{submission.carrier_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {submission.current_premium_amount != null
                          ? `$${Number(submission.current_premium_amount).toFixed(2)}`
                          : '--'}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={submission.status}
                          onChange={(e) => updateStatus(submission.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                            submission.status
                          )} border-0 cursor-pointer`}
                        >
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(submission.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => toggleExpanded(submission.id)}
                          className="text-teal-600 hover:text-teal-700 font-medium"
                        >
                          {expandedRows[submission.id] ? 'Hide Details' : 'View Details'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {expandedRows[submission.id] && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <td colSpan={6} className="px-6 py-4">
                          <ReferralActionsForSubmission
                            submissionId={submission.id}
                            submission={{
                              full_legal_name: submission.full_legal_name,
                              estimated_savings_usd: submission.estimated_savings_usd as number | null | undefined,
                              accepted_at: submission.accepted_at as string | null | undefined,
                              paid_at: submission.paid_at as string | null | undefined,
                              refunded_at: submission.refunded_at as string | null | undefined,
                            }}
                            onUpdated={(patch) => {
                              setSubmissions((prev) =>
                                prev.map((s) => (s.id === submission.id ? { ...s, ...patch } : s))
                              );
                            }}
                          />
                          <div className="space-y-3">
                            {Object.entries(submission).map(([key, value]) => {
                              if (['id', 'user_id', 'full_legal_name', 'carrier_name', 'current_premium_amount', 'status', 'created_at', 'submitted_at'].includes(key)) {
                                return null;
                              }
                              return (
                                <div key={key} className="flex justify-between">
                                  <span className="text-sm font-medium text-slate-700">
                                    {key.replace(/_/g, ' ').charAt(0).toUpperCase() +
                                      key.replace(/_/g, ' ').slice(1)}:
                                  </span>
                                  <span className="text-sm text-slate-600">
                                    {String(value || 'N/A')}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
